import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiMessageSquare, FiSend, FiCalendar, FiClock } = FiIcons;

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission - integrate with booking system
    console.log('Booking inquiry:', formData);
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'WhatsApp Booking',
      detail: '+94 77 123 4567',
      description: 'Instant responses for quick bookings'
    },
    {
      icon: FiMail,
      title: 'Email Inquiries',
      detail: 'stay@akkaradahaya.com',
      description: 'Detailed information and custom packages'
    },
    {
      icon: FiMapPin,
      title: 'Location',
      detail: 'Ullinduwawa, Kolonna',
      description: '122km from Colombo Airport'
    },
    {
      icon: FiClock,
      title: 'Response Time',
      detail: 'Within 2 hours',
      description: 'During business hours (8 AM - 8 PM)'
    }
  ];

  return (
    <section id="contact" className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white font-serif mb-6">
            Plan Your Escape
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Ready to experience the tranquility of Akkara Dahaya? Get in touch with us 
            to check availability and plan your perfect mountain retreat.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-6 font-serif">
                Contact Information
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ x: 10 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 glass-morphism rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={info.icon} className="w-6 h-6 text-emerald-300" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{info.title}</h4>
                      <p className="text-emerald-300 font-medium mb-1">{info.detail}</p>
                      <p className="text-gray-400 text-sm">{info.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Map Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="glass-card p-6 rounded-2xl"
            >
              <h4 className="text-xl font-bold text-white mb-4 font-serif">
                Getting Here
              </h4>
              <div className="space-y-3 text-gray-300">
                <p className="flex items-center gap-2">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 text-emerald-300" />
                  Via Rakwana-Deniyaya scenic route (recommended)
                </p>
                <p className="flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="w-4 h-4 text-emerald-300" />
                  3 hours from Colombo, 2 hours from Galle
                </p>
                <p className="text-sm text-gray-400 mt-4">
                  Detailed directions will be provided upon booking confirmation. 
                  The journey itself is part of the experience!
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="glass-card p-8 rounded-2xl"
          >
            <h3 className="text-2xl font-bold text-white mb-6 font-serif">
              Request to Book
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2 text-sm font-medium">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-morphism rounded-lg text-white placeholder-gray-400 border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2 text-sm font-medium">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-morphism rounded-lg text-white placeholder-gray-400 border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 text-sm font-medium">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 glass-morphism rounded-lg text-white placeholder-gray-400 border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                  placeholder="+94 77 123 4567"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2 text-sm font-medium">
                    Check-in Date *
                  </label>
                  <input
                    type="date"
                    name="checkIn"
                    value={formData.checkIn}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-morphism rounded-lg text-white border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2 text-sm font-medium">
                    Check-out Date *
                  </label>
                  <input
                    type="date"
                    name="checkOut"
                    value={formData.checkOut}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass-morphism rounded-lg text-white border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2 text-sm font-medium">
                    Guests
                  </label>
                  <select
                    name="guests"
                    value={formData.guests}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 glass-morphism rounded-lg text-white border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors"
                  >
                    <option value="">Select</option>
                    <option value="1">1 Guest</option>
                    <option value="2">2 Guests</option>
                    <option value="3">3 Guests</option>
                    <option value="4">4 Guests</option>
                    <option value="5">5 Guests</option>
                    <option value="6">6 Guests</option>
                    <option value="7">7 Guests</option>
                    <option value="8">8 Guests</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2 text-sm font-medium">
                  Special Requests or Questions
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 glass-morphism rounded-lg text-white placeholder-gray-400 border border-white/20 focus:border-emerald-300 focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your travel plans, dietary requirements, or any special occasions..."
                ></textarea>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full glass-button py-4 rounded-lg font-semibold flex items-center justify-center gap-2 pulse-glow"
              >
                <SafeIcon icon={FiSend} className="w-5 h-5" />
                Send Booking Request
              </motion.button>
            </form>

            <div className="mt-6 p-4 glass-morphism rounded-lg">
              <p className="text-gray-300 text-sm text-center">
                <SafeIcon icon={FiMessageSquare} className="w-4 h-4 inline mr-2 text-emerald-300" />
                We'll respond within 2 hours with availability and payment details
              </p>
            </div>
          </motion.div>
        </div>

        {/* Quick Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-white mb-4 font-serif">
              Need Immediate Assistance?
            </h3>
            <p className="text-gray-300 mb-6">
              For urgent bookings or last-minute changes, reach out to us directly via WhatsApp
            </p>
            
            <motion.a
              href="https://wa.me/94771234567"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 glass-button px-8 py-4 rounded-full text-lg font-semibold"
            >
              <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
              WhatsApp Us Now
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;